<template>
    <main :class="$style.page">
        <section :class="$style.page_title">
            <Typography
                is="h1"
                size="s"
                type="header"
                :responsive="false"
            >
                {{ pageTranslation.page_title }}
            </Typography>
            <Typography
                is="p"
                v-if="!appStore.isAppEnv"
                size="s"
                :responsive="false"
                :class="$style.description"
            >
                {{ pageTranslation.page_description }}
            </Typography>
        </section>
        <Typography
            is="ul"
            type="label"
            size="m"
            :class="$style.categories"
        >
            <li
                v-for="(item, index) in currentCategoryInfo"
                :key="index"
                :class="$style.category_item"
            >
                <Link
                    :to="item.href"
                    color="purple-600"
                    :data-interface-target="currentCategoryType"
                    :class="$style.category_link"
                >
                    {{ item.title }}
                </Link>
            </li>
        </Typography>
        <TheEnd />
    </main>
</template>
<script lang="ts" setup>
import { useQuery } from '@tanstack/vue-query'
import { useHead } from '@unhead/vue'
import { computed, onServerPrefetch } from 'vue'
import { useRoute } from 'vue-router'

import Link from '@/components/Link.vue'
import TheEnd from '@/components/TheEnd/TheEnd.vue'
import Typography from '@/components/Typography.vue'
import { useUrl } from '@/composables/url-helpers'
import { useTr } from '@/composables/use-tr'
import { useI18nAddon } from '@/i18n/i18n'
import { usePersisterFn } from '@/query/persister-plugin'
import { Persisters } from '@/query/shared'
import { ROUTE_NAMES, type ROUTE_NAMES_VALUES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import type { APICategoryShortInfo, CategoryShortInfo, CategoryType } from '@/types'
import { ApiRequestError } from '@/utils/requests/api-error'
import { useAPI } from '@/utils/requests/api-plugin'

const MAP_TO_API_CATEGORY: Partial<Record<ROUTE_NAMES_VALUES, CategoryType>> = {
    [ROUTE_NAMES.ALL_TAGS]: 'tag',
    [ROUTE_NAMES.ALL_CATEGORIES]: 'category',
    [ROUTE_NAMES.ALL_SERIES]: 'series',
}

const tr = useTr()
const route = useRoute()
const { getGamesCategoriesShortInfo } = useAPI()
const { getCategoryRoute } = useUrl()
const { currentLocale } = useI18nAddon()
const appStore = useAppStore()

const currentCategoryType = computed(() => MAP_TO_API_CATEGORY[route.name as ROUTE_NAMES_VALUES] as CategoryType)
function setCategoriesInfo(result: APICategoryShortInfo[]): CategoryShortInfo[] {
    return result.map((catInfo) => ({
        ...catInfo,
        href: getCategoryRoute({ hru: catInfo.hru, type: currentCategoryType.value }),
    }))
}

// persister type depends on queryKey cardinality
const queryKey = computed(() => ['all-categories', currentCategoryType.value, currentLocale.value])
const queryPersister = usePersisterFn(Persisters.LowCardinality)

const { data: currentCategoryInfo, suspense } = useQuery({
    queryKey,
    queryFn: async () => {
        const result = await getGamesCategoriesShortInfo(currentCategoryType.value, currentLocale.value)

        if (result.status !== 'ok') {
            throw new ApiRequestError(result)
        }

        return setCategoriesInfo(result.data)
    },
    placeholderData: () => [],
    persister: queryPersister,
})

const pageTranslation = computed(() => {
    switch (currentCategoryType.value) {
        case 'tag':
            return {
                page_title: tr('gamesCategories.tags_page_title'),
                page_description: tr('gamesCategories.tags_page_description'),
            }
        case 'series':
            return {
                page_title: tr('gamesCategories.series_page_title'),
                page_description: tr('gamesCategories.series_page_description'),
            }
        default:
            return {
                page_title: tr('gamesCategories.category_page_title'),
                page_description: tr('gamesCategories.category_page_description'),
            }
    }
})

onServerPrefetch(suspense)

const metaTitle = computed(() => {
    switch (currentCategoryType.value) {
        case 'tag':
            return tr('gamesCategories.tags_meta_title')
        case 'series':
            return tr('gamesCategories.series_meta_title')
        default:
            return tr('gamesCategories.category_meta_title')
    }
})

const metaDescription = computed(() => {
    switch (currentCategoryType.value) {
        case 'tag':
            return tr('gamesCategories.tags_meta_description')
        case 'series':
            return tr('gamesCategories.series_meta_description')
        default:
            return tr('gamesCategories.category_meta_description')
    }
})
const pageMeta = computed(() => {
    return {
        title: () => metaTitle.value,
        meta: [
            {
                name: 'title',
                content: metaTitle.value,
            },
            {
                name: 'description',
                content: metaDescription.value,
            },
            {
                property: 'og:title',
                content: metaTitle.value,
            },
            {
                property: 'og:description',
                content: metaDescription.value,
            },
            {
                name: 'twitter:title',
                content: metaTitle.value,
            },
            {
                name: 'twitter:description',
                content: metaDescription.value,
            },
        ],
    }
})

useHead(pageMeta)
</script>
<style module>
.page {
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.page_title {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 0 0;
}

.description {
    color: var(--white-40);
}

.categories {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(175px, 1fr));
}

.category_item {
    border-bottom: 1px solid var(--white-08);
    display: flex;
    align-items: center;
}

.category_link {
    padding: 16px 16px 16px 0;
    width: 100%;
}

@media (--desktop-small-plus) {
    .page {
        gap: 24px;
    }

    .page_title {
        padding: unset;
    }

    .categories {
        grid-template-columns: repeat(auto-fit, minmax(201px, 1fr));
    }
}
</style>
